import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkAndGenerateApiKey, linkedAccountSignup } from "../../store/actions/tenantAction";
import { SHOW_SNACKBAR } from "../../utils/types";
import { useTranslation } from "react-i18next";
import { encryptData, getRandomString, toTitleCase } from "../../utils/helper";

function WebLiveChatConnect({ setIsShow, onIntegrated, isConnected, selectedAccount, selectedLinkAccount }) {
    const { t } = useTranslation();
    let dispatch = useDispatch();
    const [apiKey, setApiKey] = useState({});
    const tenantReducer = useSelector((state) => state.tenantReducer);
    const globalReducer = useSelector((state) => state.globalReducer);
    const [accountInfo, setAccountInfo] = useState({});
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        getKeys();
    }, []);

    const getKeys = () => {
        const expiry = new Date();
        expiry.setMonth(expiry.getMonth() + 12);
        dispatch(
            checkAndGenerateApiKey(
                {
                    tenantId: globalReducer.loginInfo.tenantId,
                    apiKey: getRandomString(75),
                    expiry: expiry,
                },
                setApiKey
            )
        );
    };

    const handleMessage = () => {
        dispatch(
            linkedAccountSignup(
                {
                    accessToken: navigator.userAgent,
                    accountType: "WEBLIVECHAT",
                    accountId: navigator.userAgent,
                    businessName: accountInfo?.businessName,
                    accountName: accountInfo?.accountName,
                    bspId: tenantReducer.tenantDetails?.bsp?.id,
                    tenantId: tenantReducer.tenantDetails?.id,
                    tenantWabaId: globalReducer.waba?.id,
                },
                (r) => {
                    dispatch({
                        type: SHOW_SNACKBAR,
                        payload: {
                            type: "success",
                            message: "Web Live Chat Connected Successfully.",
                            open: true,
                        },
                    });
                    setIsShow(false);
                    onIntegrated(r);
                }
            )
        );
    }

    const handleUrlCopy = () => {
        navigator.clipboard
            .writeText(getScriptCode())
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 5000); // Reset after 2 seconds
            })
            .catch((error) => console.error("Copy failed", error));
    };

    const getScriptCode = () => {
        return `<script>
    window.LiveChatConfig = {
      tenantId: "${selectedLinkAccount?.tenantId}",
      apiKey: "${encryptData(apiKey?.apiKey)}",
      tenantWabaId: ${selectedAccount?.id},
      theme: {
        primaryColor: "#10b981",
        textColor: "#ffffff",
        position: "right",        // left | right
        headerTitle: "iOmniHub Support"
      }
    };
  </script>
  <script src="https://live.iomnihub.ai/sdk/livechat-sdk.js"></script>`;
    };

    return (
        <div className="bg-white overflow-auto">
            {isConnected ? <div>
                <div className="pt-2 pr-4 pl-4">
                    <div className="h-full">
                        <div className="text-[14px] min-h-[550px]">
                            <label className="font-semibold text-[16px]">
                                {t('Connected Web LiveChat')}
                            </label>
                            <div className="text-gray-500">
                                <p className="mt-2 text-[13px]">
                                    {t('To integrate Web Live Chat with iOmnihub, follow the Web SDK integration details provided below.')}
                                </p>
                                <div className="mt-3 grid grid-cols-2 gap-4 w-[50%] mb-3">
                                    <div className="text-gray-700">Account Name :</div>
                                    <div>{selectedLinkAccount?.accountName}</div>
                                    <div className="text-gray-700">Business Name :</div>
                                    <div>{selectedLinkAccount?.businessName}</div>
                                    <div className="text-gray-700">X-Tenant-Id :</div>
                                    <div>{selectedLinkAccount?.tenantId}</div>
                                </div>
                                <p className="mt-8 text-[13px] font-semibold">
                                    {t('Copy the following script and paste it inside the HTML <body> section to enable the Web SDK.')}
                                </p>
                                <div className="w-full mt-2">
                                    <div className="">
                                        <button
                                            type="button"
                                            onClick={handleUrlCopy}
                                            className="text-green-500 bg-green-50 border-2 border-green-300 rounded-[4px] px-4 py-1"
                                        >
                                            {isCopied ? "Copied" : "Copy"}
                                        </button>
                                    </div>
                                    <div className="overflow-y-auto">
                                        <pre style={{ background: "#f5f5f5", padding: "10px" }}>
                                            <code className="text-wrap">{getScriptCode()}</code>
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div> :
                <form
                    className="pt-2 pr-4 pl-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleMessage();
                    }}
                >
                    <div>
                        <div className="text-[14px] min-h-[200px]">
                            <label className="font-semibold text-[16px]">
                                {t('Connect Web LiveChat')}
                            </label>
                            <div className="text-gray-500">
                                <p className="mt-2 text-[13px]">
                                    {t('To integrate Web Live Chat with iOmnihub, follow the Web SDK integration details provided below.')}
                                </p>
                                <ul className="mt-2 text-[13px] list-disc ml-7">
                                    <li>
                                        {t("Web LiveChat is a real-time messaging solution that connects website visitors with your support or sales team instantly")}
                                    </li>
                                </ul>
                                <div className="mt-6">
                                    <label className="text-gray-700">Business Name</label>
                                    <input type="text" required placeholder="Enter Business Name" className="border border-gray-300 rounded-md py-2 text-[14px] w-[100%]"
                                        value={accountInfo?.businessName} onChange={(e) => setAccountInfo({ ...accountInfo, businessName: e.target.value })} />
                                    <div className="mt-4">
                                        <label className="text-gray-700">Account Name</label>
                                        <input type="text" required placeholder="Enter Account Name" className="border border-gray-300 rounded-md py-2 text-[14px] w-[100%]"
                                            value={accountInfo?.accountName} onChange={(e) => setAccountInfo({ ...accountInfo, accountName: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="flex w-fit py-2 items-center px-4 bg-theme rounded-md text-white font-medium text-[16px] gap-2 mt-3">
                                <div>
                                    <img className="w-[25px] h-[25px] box-border" src={`${process.env.PUBLIC_URL}/images/live-chat.png`} />
                                </div>
                                <button type="submit" className="text-[14px] transition-all ease-in-out rounded cursor-pointer">
                                    {t('Connect Web LiveChat')}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>}
        </div>
    );
}

export default WebLiveChatConnect;
